/**
 * *Widget caps* — `mockups/client/settings.html`, the last section.
 *
 * "A cap limits your worst case, not your expected use." Settings owns the cap and
 * the worst case; the two widget pages own this month's usage and are linked, not
 * repeated (DESIGN.md P2). Nothing here is a meter and nothing here is a chart: how
 * close a widget is to its cap is `client/widget-*.html`'s answer, and restating it
 * would put a second owner on it.
 *
 * How usage is billed is the billing page's to say (session 54); this section is the
 * caps, what each one can cost, and what happens past it.
 */

import type { UsageSummaryView, WidgetView } from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import type { UseFormReturn } from 'react-hook-form'

import { Input } from '../../../components/portal/Form.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { Row, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import { Tag } from '../../../components/portal/Status.js'
import { B, More, Muted, Num } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { capOf, worstCaseAt, type CapsFormInput } from './caps.js'

/**
 * The anchor this section is linked to, and the id it renders.
 *
 * "Change the cap" on both widget pages links here rather than editing:
 * `client/settings.html` §Widget caps is where a cap is set, and its own note says
 * the widget pages "own this month's usage; they are linked, not repeated". The
 * constant lives with the section that owns the anchor, so a rename is one edit —
 * `pages/client/widget/WidgetPause.tsx` re-exports it for its own three call sites.
 */
export const WIDGET_CAPS_ANCHOR = 'widget-caps'

export type WidgetCapsPanelProps = {
  readonly widgets: readonly WidgetView[]
  readonly mode: UsageSummaryView['mode']
  readonly form: UseFormReturn<CapsFormInput, unknown, CapsFormInput>
  /** False for anybody without `money:approve`: the caps are read, not typed. */
  readonly maySetCap: boolean
}

/**
 * The field path for one row of the caps array.
 *
 * A helper rather than an inline template, because `restrict-template-expressions`
 * refuses a number in one and `String(index)` widens the literal to
 * `` `caps.${string}.monthlyCap` ``, which is not a `FieldPath` of this form. Both
 * halves of that are here, once.
 */
function capPath(index: number): `caps.${number}.monthlyCap` {
  return `caps.${String(index)}.monthlyCap` as `caps.${number}.monthlyCap`
}

export function WidgetCapsPanel({ widgets, mode, form, maySetCap }: WidgetCapsPanelProps) {
  const format = useFormat()

  /*
   * `watch` is typed as though the field were always there and answers `undefined`
   * on the render before `values` lands — which is every first render, because the
   * widgets arrive from a query. The cast is what lets the `??` be honest.
   */
  const typed = (form.watch('caps') as CapsFormInput['caps'] | undefined) ?? []

  return (
    <Section id={WIDGET_CAPS_ANCHOR}>
      <SectionHead
        title="Widget caps"
        note={
          mode === 'postpay'
            ? 'Postpay — you hold an active retainer'
            : 'Prepay — billed against your balance'
        }
        pushNote
      />

      <Region>
        <Row>
          {widgets.map((widget) => (
            <More key={widget.ref} asChild>
              <Link to="/app/widgets/$widgetRef" params={{ widgetRef: widget.ref }}>
                {widget.name} usage →
              </Link>
            </More>
          ))}
        </Row>

        <RegionCols>
          {widgets.map((widget, index) => {
            /*
             * The worst case is the **API's** figure until the reader changes the
             * cap (`.docs/19`: "computed by the API and returned — the page does not
             * compute"). `worstCaseAt` is only reached for a number nobody has
             * priced yet, which is the one case no route can answer.
             */
            const row = typed[index]
            const typedCap = row === undefined ? widget.monthly_cap : capOf(row.monthlyCap)
            const worst =
              typedCap === widget.monthly_cap ? widget.worst_case : worstCaseAt(widget, typedCap)

            return (
              <Stack key={widget.ref} gap="tight">
                <Row>
                  <B>{widget.name}</B>
                  <Tag>{widget.site}</Tag>
                </Row>

                {maySetCap ? (
                  <Input
                    control={form.control}
                    name={capPath(index)}
                    label={`Monthly cap — ${widget.unit_noun}s a month`}
                    hint={`At least the ${format.number(widget.included_qty)} your package includes.`}
                  />
                ) : null}

                <Kv bare>
                  {/*
                   * The read-only shape, not a disabled box: `.docs/19` §Edge cases
                   * asks for the buttons a reader would be refused for to be absent,
                   * and a cap they may not set is a fact rather than a field.
                   */}
                  {maySetCap ? null : (
                    <KvRow
                      k="Monthly cap"
                      v={
                        <>
                          <Num value={widget.monthly_cap} /> {widget.unit_noun}s a month
                        </>
                      }
                    />
                  )}
                  <KvRow
                    k="Worst case at this cap"
                    v={worst === null ? <Muted>—</Muted> : <Num value={worst} />}
                  />
                  <KvRow k="Past the cap" v="The widget shows its fallback" />
                </Kv>
              </Stack>
            )
          })}
        </RegionCols>
      </Region>
    </Section>
  )
}
